import { Users } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SaveStatus } from "@/components/SaveStatus";
import { toast } from "@/components/ui/sonner";
import { useAutosave } from "@/lib/hooks/useAutosave";
import { useAuth } from "@/lib/auth/auth-context";
import { patchMe } from "@/lib/auth/auth-api";
import { queryKeys } from "@/lib/query/keys";

import { FormSection } from "./controls";
import { patchProfile, qKeys } from "../api";

/** Uppercase micro-label, matching ProfileFields. */
const LABEL = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";

interface RespondentValues {
  first_name: string;
  last_name: string;
  position: string;
}

/**
 * The person filling the diagnostic — first/last name + position. These live on
 * the profile's User. Parity with the old identification "répondant" fields.
 *
 * Save target depends on mode:
 *  - **Edit** (a profile exists): save through the enterprise-profile endpoint,
 *    which is scoped to the effective user. In partner "on behalf of" mode that
 *    is the *member's* profile, so respondent edits land on the member's record
 *    — not the partner's (which patching /me would do). Initial values come from
 *    the loaded profile so we display the member's respondent, not the partner.
 *  - **Create** (no profile yet): always the current user (creation is only
 *    reachable in self mode), so autosave to /me.
 */
/** What a parent can ask of the section once it holds a ref to it. */
export interface RespondentHandle {
  /**
   * Persist a debounced change now, and say whether it landed.
   *
   * The section autosaves 400 ms after the last keystroke and is unmounted by
   * whatever replaces the form on success, which cancels a pending timer
   * without sending it. A parent that treats these fields as required has to
   * flush before it acts, or a name typed just before the button is clicked
   * passes validation and is never written.
   */
  flush: () => Promise<boolean>;
}

export const RespondentSection = forwardRef<
  RespondentHandle,
  {
    profileId?: string;
    initial?: RespondentValues;
    /** Mirror the current values to a parent that has to validate them before
     *  letting a form be submitted. The section keeps ownership of its own
     *  state and autosave; this only reports. */
    onChange?: (values: RespondentValues) => void;
    errors?: Partial<Record<keyof RespondentValues, string>>;
  }
>(function RespondentSection({ profileId, initial, onChange, errors }, ref) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const failed = useRef(false);

  const autosave = useAutosave<RespondentValues>({
    save: async (data, signal) => {
      if (profileId) {
        await patchProfile(
          profileId,
          {
            respondent_first_name: data.first_name,
            respondent_last_name: data.last_name,
            respondent_position: data.position,
          },
          signal,
        );
        await qc.invalidateQueries({ queryKey: qKeys.profile(profileId) });
      } else {
        await patchMe(data);
        await qc.invalidateQueries({ queryKey: queryKeys.auth.me() });
      }
    },
    onSaved: () => {
      failed.current = false;
    },
    onError: () => {
      // `useAutosave` swallows the rejection — it reports through status and
      // this callback — so a flush resolves either way. The flag is how the
      // handle below tells a caller the write did not land.
      failed.current = true;
      toast.error(t("questionnaire.saveError"));
    },
  });

  useImperativeHandle(ref, () => ({
    flush: async () => {
      failed.current = false;
      await autosave.flush();
      return !failed.current;
    },
  }));

  const [values, setValues] = useState<RespondentValues>(
    initial ?? {
      first_name: user?.first_name ?? "",
      last_name: user?.last_name ?? "",
      position: user?.position ?? "",
    },
  );

  const patch = (p: Partial<RespondentValues>) => {
    const next = { ...values, ...p };
    setValues(next);
    onChange?.(next);
    autosave.save(next);
  };

  // Report the initial values too, so a parent validating on submit sees what
  // is already on screen rather than only what the person went on to edit.
  useEffect(() => {
    onChange?.(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const field = (key: keyof RespondentValues, labelKey: string, required = false) => {
    const id = `respondent-${key}`;
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id} className={LABEL}>
          {t(labelKey)}
          {required && <span aria-hidden className="text-primary"> *</span>}
        </Label>
        <Input
          id={id}
          value={values[key]}
          onChange={(e) => patch({ [key]: e.target.value })}
          aria-invalid={errors?.[key] ? true : undefined}
        />
        {errors?.[key] && (
          <p className="text-xs text-destructive-ink">{errors[key]}</p>
        )}
      </div>
    );
  };

  return (
    <FormSection
      tone="social"
      icon={<Users aria-hidden />}
      title={t("questionnaire.identification.respondent")}
      description={t("questionnaire.identification.respondentHint")}
      aside={<SaveStatus status={autosave.status} />}
    >
      <div className="grid gap-4 sm:grid-cols-3">
        {field("first_name", "questionnaire.identification.respondentFirstName", true)}
        {field("last_name", "questionnaire.identification.respondentLastName", true)}
        {field("position", "questionnaire.identification.respondentPosition")}
      </div>
    </FormSection>
  );
});

export type { RespondentValues };
